import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router';
import Layout, { UserLayout } from 'src/layouts';
import SuspenseProgressBridge from 'src/components/SuspenseProgressBridge';
import ProtectedRoute from 'src/router/ProtectedRoute';
import { protectedRoutes, publicRoutes } from 'src/router/routes';
import { routeType } from 'src/utils/proptypes';

interface OrgThemeColors {
   lightPrimary?: string;
   primary?: string;
   secondary?: string;
   view?: string;
   lightView?: string;
   callToAction?: string;
   headerText?: string;
   heroText?: string;
   startBackground?: string;
   endBackground?: string;
}

interface SiteVisualBranding {
   pallate?: {
      colorLightPrimary?: string;
      colorPrimary?: string;
      colorSecondary?: string;
      colorView?: string;
      colorLightView?: string;
      colorCallToAction?: string;
      colorHeaderText?: string;
      colorHeroText?: string;
      colorStartBackground?: string;
      colorEndBackground?: string;
   };
}

const NotFoundPage = lazy(() => import('src/pages/notFound'));

// <title> used to be mutated imperatively in this component on every
// route change. Removed in NPT-1072 — titles are now SSR-emitted via
// the per-route +title.ts files (see pages/) and per-page <SEO>
// components inside the route tree. The old imperative writes
// OVERRODE per-page titles after hydration with the route's static
// `title:` field (e.g. "Nonprofit Trips - Hotel Detail"), silently
// breaking per-instance tab titles for /c/:slug/hotels/:id etc.
//
// Tradeoff: admin and auth routes that don't have their own SEO
// component now show the brand-default tab title. If we want
// per-page admin tab titles back, add <title> declarations directly
// to those route components (React 19 metadata hoisting will pick
// them up on hydration).

const Router = () => {
   return (
      <Routes>
         {publicRoutes.map((route: routeType, idx: number) => {
            return <Route path={route.path} element={<Layout route={route} />} key={idx} />;
         })}
         {protectedRoutes.map((route: routeType, idx: number) => {
            return (
               <Route
                  key={idx}
                  path={route.path}
                  element={
                     <ProtectedRoute requiredPermissions={route.permissions || []}>
                        <Layout route={route} />
                     </ProtectedRoute>
                  }
               />
            );
         })}
         <Route
            path="*"
            element={
               <UserLayout>
                  <Suspense fallback={<SuspenseProgressBridge />}>
                     <NotFoundPage />
                  </Suspense>
               </UserLayout>
            }
         />
      </Routes>
   );
};

export default Router;
